import React, { useEffect, useState } from 'react';
import Swal from 'sweetalert2';
import { FiPlus, FiEye, FiX } from 'react-icons/fi';
import { feePaymentsService, feeStructuresService } from '../../services/api';
import { formatFcfa, StatusBadge } from '../../utils/feeUtils';
import { useAcademicYear } from '../../contexts/AcademicYearContext';
import ConditionalButton from '../../components/common/ConditionalButton';
import InscriptionSelect from '../../components/fees/InscriptionSelect';

const FeePaymentsPage = () => {
  const { selectedYear } = useAcademicYear();
  const [payments, setPayments] = useState([]);
  const [totals, setTotals] = useState({ jour: 0, mois: 0, annee: 0 });
  const [typeReglements, setTypeReglements] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showDetail, setShowDetail] = useState(null);
  const [selectedInscription, setSelectedInscription] = useState(null);
  const [installments, setInstallments] = useState([]);
  const [summary, setSummary] = useState(null);
  const [form, setForm] = useState({
    inscription_id: '', amount: '', amount_received: '', type_reglement_id: 1, payment_reference: '',
  });
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');

  const fetchPayments = async () => {
    const params = { search };
    if (selectedYear) params.annee_scolaire_id = selectedYear.id;
    const res = await feePaymentsService.getAll(params);
    setPayments(res.data.data || []);
    setTotals(res.data.totals || { jour: 0, mois: 0, annee: 0 });
  };

  useEffect(() => {
    fetchPayments();
    feePaymentsService.getTypeReglements().then((r) => setTypeReglements(r.data.data || []));
  }, [selectedYear]);

  const openPaymentModal = () => {
    setSelectedInscription(null);
    setInstallments([]);
    setSummary(null);
    setForm({ inscription_id: '', amount: '', amount_received: '', type_reglement_id: 1, payment_reference: '' });
    setShowModal(true);
  };

  const selectInscription = async (insc) => {
    setSelectedInscription(insc);
    setForm((prev) => ({ ...prev, inscription_id: insc?.id || '' }));
    if (!insc?.id) {
      setInstallments([]);
      setSummary(null);
      return;
    }
    try {
      const res = await feeStructuresService.getStudentInstallments(insc.id);
      setInstallments(res.data.data || []);
      setSummary(res.data.summary || null);
    } catch {
      setInstallments([]);
      setSummary(null);
    }
  };

  const handlePayment = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        ...form,
        amount: parseInt(form.amount),
        amount_received: parseInt(form.amount_received || form.amount),
        idempotency_key: `pay-${form.inscription_id}-${Date.now()}`,
      };
      const res = await feePaymentsService.create(payload);
      const allocations = res.data.data?.allocations || [];
      const allocText = allocations.map((a) =>
        `${a.category_name} — ${a.label}: ${formatFcfa(a.amount)}`
      ).join('\n');

      Swal.fire({
        icon: 'success',
        title: 'Paiement enregistré',
        html: `<p>Reçu: <strong>${res.data.data?.payment?.receipt_number || ''}</strong></p>
               ${allocText ? `<pre class="text-left text-sm mt-2">${allocText}</pre>` : ''}
               ${res.data.data?.credit ? `<p class="mt-2">Avoir: ${formatFcfa(res.data.data.credit.amount)}</p>` : ''}`,
      });
      setShowModal(false);
      setForm({ inscription_id: '', amount: '', amount_received: '', type_reglement_id: 1, payment_reference: '' });
      setSelectedInscription(null);
      setInstallments([]);
      fetchPayments();
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Erreur', text: err.response?.data?.message });
    } finally {
      setLoading(false);
    }
  };

  const viewDetail = async (id) => {
    const res = await feePaymentsService.getById(id);
    setShowDetail(res.data.data);
  };

  const handleCancel = async (id) => {
    const { value: reason } = await Swal.fire({
      title: 'Annuler ce paiement',
      input: 'textarea',
      inputLabel: 'Motif d\'annulation (obligatoire)',
      inputValidator: (v) => !v && 'Le motif est requis',
      showCancelButton: true,
    });
    if (!reason) return;
    try {
      await feePaymentsService.cancel(id, { reason });
      setShowDetail(null);
      fetchPayments();
      Swal.fire({ icon: 'success', title: 'Paiement annulé', timer: 2000, showConfirmButton: false });
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Erreur', text: err.response?.data?.message });
    }
  };

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Encaissements</h1>
        <ConditionalButton entity="frais-scolarite" permission="create"
          onClick={openPaymentModal}
          className="flex items-center space-x-2 bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700">
          <FiPlus /><span>Nouvel encaissement</span>
        </ConditionalButton>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        {['jour', 'mois', 'annee'].map((period) => (
          <div key={period} className="bg-white rounded-lg shadow p-6">
            <p className="text-sm font-medium text-gray-600">
              Total {period === 'jour' ? 'du jour' : period === 'mois' ? 'du mois' : "de l'année"}
            </p>
            <p className="text-2xl font-semibold text-gray-900">{formatFcfa(totals[period])}</p>
          </div>
        ))}
      </div>

      <div className="mb-4">
        <input type="text" placeholder="Rechercher un élève..." value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && fetchPayments()}
          className="border rounded-md p-2 w-full max-w-md" />
      </div>

      <div className="bg-white shadow rounded-lg overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Élève</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Classe</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Montant</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Mode</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reçu</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {payments.map((p) => (
              <tr key={p.id}>
                <td className="px-4 py-3 text-sm">{new Date(p.paid_at).toLocaleDateString('fr-FR')}</td>
                <td className="px-4 py-3 text-sm">{p.eleve_nom} {p.eleve_prenom}</td>
                <td className="px-4 py-3 text-sm text-gray-500">{p.classe}</td>
                <td className="px-4 py-3 text-sm font-medium">{formatFcfa(p.total_amount)}</td>
                <td className="px-4 py-3 text-sm">{p.type_reglement}</td>
                <td className="px-4 py-3 text-sm text-gray-500">{p.receipt_number}</td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => viewDetail(p.id)} className="text-primary-600 hover:text-primary-900">
                    <FiEye />
                  </button>
                </td>
              </tr>
            ))}
            {payments.length === 0 && (
              <tr><td colSpan="7" className="px-4 py-8 text-center text-gray-500">Aucun encaissement</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Modal encaissement */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">Nouvel encaissement</h2>
              <button onClick={() => setShowModal(false)}><FiX className="h-5 w-5" /></button>
            </div>
            <form onSubmit={handlePayment} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Élève inscrit
                  {selectedYear && (
                    <span className="text-gray-400 font-normal ml-1">({selectedYear.annee_scolaire})</span>
                  )}
                </label>
                <InscriptionSelect
                  selectedYear={selectedYear}
                  value={selectedInscription}
                  onChange={selectInscription}
                />
                {selectedInscription && (
                  <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-md text-sm text-green-800">
                    <strong>{selectedInscription.nom} {selectedInscription.prenom}</strong>
                    <span className="text-green-700">
                      {' '}— {selectedInscription.matricule}
                      {selectedInscription.classe_nom ? ` — ${selectedInscription.classe_nom}` : ''}
                    </span>
                  </div>
                )}
              </div>

              {summary && (
                <div className="bg-gray-50 rounded-lg p-4 text-sm">
                  <div className="grid grid-cols-2 gap-2">
                    <span>Total dû : <strong>{formatFcfa(summary.total_due)}</strong></span>
                    <span>Total payé : <strong>{formatFcfa(summary.total_paid)}</strong></span>
                    <span>Reste à payer : <strong className="text-red-600">{formatFcfa(summary.outstanding)}</strong></span>
                    <span>Impayé échu : <strong className="text-red-600">{formatFcfa(summary.overdue_amount)}</strong></span>
                  </div>
                </div>
              )}

              {installments.length > 0 && (
                <div className="max-h-40 overflow-y-auto">
                  <table className="min-w-full text-xs">
                    <thead><tr className="text-gray-500">
                      <th className="text-left p-1">Rubrique</th><th className="text-left p-1">Échéance</th>
                      <th className="text-right p-1">Dû</th><th className="text-right p-1">Payé</th><th className="p-1">Statut</th>
                    </tr></thead>
                    <tbody>
                      {installments.map((i) => (
                        <tr key={i.id} className="border-t">
                          <td className="p-1">{i.category_name}</td>
                          <td className="p-1">{i.label}</td>
                          <td className="p-1 text-right">{formatFcfa(i.amount_due)}</td>
                          <td className="p-1 text-right">{formatFcfa(i.amount_paid)}</td>
                          <td className="p-1"><StatusBadge status={i.status} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Montant versé (FCFA)</label>
                  <input type="number" required min="1" value={form.amount}
                    onChange={(e) => setForm({ ...form, amount: e.target.value, amount_received: e.target.value })}
                    className="mt-1 w-full border rounded-md p-2" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Somme reçue (FCFA)</label>
                  <input type="number" min="0" value={form.amount_received}
                    onChange={(e) => setForm({ ...form, amount_received: e.target.value })}
                    className="mt-1 w-full border rounded-md p-2" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Mode de règlement</label>
                  <select value={form.type_reglement_id}
                    onChange={(e) => setForm({ ...form, type_reglement_id: e.target.value })}
                    className="mt-1 w-full border rounded-md p-2">
                    {typeReglements.map((t) => <option key={t.id} value={t.id}>{t.type_reglement}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Référence (chèque, Mobile Money...)</label>
                  <input type="text" value={form.payment_reference}
                    onChange={(e) => setForm({ ...form, payment_reference: e.target.value })}
                    className="mt-1 w-full border rounded-md p-2" />
                </div>
              </div>

              <button type="submit" disabled={loading || !form.inscription_id}
                className="w-full bg-primary-600 text-white py-2 rounded-lg hover:bg-primary-700 disabled:opacity-50">
                Enregistrer le paiement
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Modal détail */}
      {showDetail && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">Détail du paiement</h2>
              <button onClick={() => setShowDetail(null)}><FiX className="h-5 w-5" /></button>
            </div>
            <div className="space-y-2 text-sm">
              <p><strong>Élève :</strong> {showDetail.eleve_nom} {showDetail.eleve_prenom}</p>
              <p><strong>Montant :</strong> {formatFcfa(showDetail.total_amount)}</p>
              <p><strong>Reçu :</strong> {showDetail.receipt_number}</p>
              <p><strong>Date :</strong> {new Date(showDetail.paid_at).toLocaleString('fr-FR')}</p>
              <p><strong>Mode :</strong> {showDetail.type_reglement}</p>
              {showDetail.allocations?.length > 0 && (
                <div className="mt-4">
                  <p className="font-medium mb-2">Ventilation :</p>
                  {showDetail.allocations.map((a) => (
                    <div key={a.id} className="flex justify-between text-gray-600">
                      <span>{a.category_name} — {a.label}</span>
                      <span>{formatFcfa(a.amount)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {showDetail.status === 'validated' && (
              <ConditionalButton entity="frais-scolarite" permission="update"
                onClick={() => handleCancel(showDetail.id)}
                className="mt-4 w-full bg-red-600 text-white py-2 rounded-lg hover:bg-red-700">
                Annuler (contre-passation)
              </ConditionalButton>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default FeePaymentsPage;
